import { useMutation } from '@tanstack/react-query';
import { createContext, useContext, useMemo, useReducer, useRef } from 'react';

import { describeApiError } from '../api-error';

import { reduceActions } from './log';

import type { Action } from './log';
import type {
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import type { ReactNode } from 'react';

/**
 * The one place a mutation says it started and how it ended — GEN-41.
 *
 * `useTrackedMutation` is deliberately the *only* way the console mutates: a screen that
 * reaches for `useMutation` directly gets no feedback, and that is exactly the state this
 * replaced. `no-raw-mutation.test.ts` is what keeps it true.
 */

interface Actions {
  readonly actions: readonly Action[];
  readonly record: (name: string) => (reason?: string) => void;
  /** Take one off the feed. A refusal that has been read is a refusal that is done. */
  readonly dismiss: (id: number) => void;
}

const ActionsContext = createContext<Actions | undefined>(undefined);

export function ActionsProvider({ children }: { children: ReactNode }) {
  const [actions, dispatch] = useReducer(reduceActions, []);

  // A ref rather than state: the id must not re-render anything, and two actions started
  // in the same tick must not receive the same one.
  const next = useRef(0);

  const value = useMemo<Actions>(
    () => ({
      actions,
      dismiss: (id: number) => {
        dispatch({ kind: 'dismissed', id });
      },
      record: (name: string) => {
        const id = (next.current += 1);
        dispatch({ kind: 'started', id, name });

        return (reason?: string) => {
          dispatch({
            kind: 'settled',
            id,
            ...(reason === undefined ? {} : { reason }),
          });
        };
      },
    }),
    [actions],
  );

  return (
    <ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>
  );
}

export function useActions(): Actions {
  const value = useContext(ActionsContext);

  if (value === undefined)
    throw new Error('useActions needs an ActionsProvider above it');

  return value;
}

/**
 * A mutation that reports itself.
 *
 * `name` is what the operator asked for in their words — "Save title", not
 * `updateLesson`. The failure text is `describeApiError`, so a refusal names the field
 * and the observed value rather than saying an error occurred.
 *
 * **The work is wrapped, not the lifecycle callbacks.** Each call gets its own `settle`
 * in its own closure, so two saves in flight settle independently — a single ref across
 * `onMutate`/`onSettled` lets the second overwrite the first, leaving the first on the
 * feed as `working` forever. It also leaves the caller's callbacks alone.
 */
export function useTrackedMutation<
  TData = unknown,
  TError = Error,
  // `void` so a mutation taking no argument is still called as `mutate()`, which is how
  // every caller that predates this wrapper already calls it.
  TVariables = void,
>(
  name: string,
  options: UseMutationOptions<TData, TError, TVariables> & {
    mutationFn: (variables: TVariables) => Promise<TData>;
  },
): UseMutationResult<TData, TError, TVariables> {
  const { record } = useActions();
  const { mutationFn } = options;

  return useMutation({
    ...options,
    mutationFn: async (variables: TVariables) => {
      const settle = record(name);

      try {
        const data = await mutationFn(variables);
        settle();

        return data;
      } catch (failure) {
        settle(describeApiError(failure));
        throw failure;
      }
    },
  });
}
